import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { checklistService } from '@/services/checklistService'
import type { ChecklistInsert, ChecklistUpdate, ChecklistItemUpdate } from '@/types/database'
import type { ChecklistEvidenceInsert } from '@/types/checklistEvidence'

export const CHECKLISTS_QUERY_KEY = ['checklists']
export const TEMPLATES_QUERY_KEY = ['checklist-templates']
export const CHECKLIST_EVIDENCE_QUERY_KEY = ['checklist-evidence']

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function useChecklists() {
  return useQuery({
    queryKey: CHECKLISTS_QUERY_KEY,
    queryFn: () => checklistService.getChecklists(),
  })
}

export function useChecklist(id: string) {
  return useQuery({
    queryKey: [...CHECKLISTS_QUERY_KEY, id],
    queryFn: () => checklistService.getChecklist(id),
    enabled: UUID_RE.test(id),
  })
}

export function useChecklistItems(checklistId: string) {
  return useQuery({
    queryKey: [...CHECKLISTS_QUERY_KEY, checklistId, 'items'],
    queryFn: () => checklistService.getChecklistItems(checklistId),
    enabled: UUID_RE.test(checklistId),
  })
}

export function useChecklistsByProduct(productId: string) {
  return useQuery({
    queryKey: [...CHECKLISTS_QUERY_KEY, 'product', productId],
    queryFn: () => checklistService.getChecklistsByProduct(productId),
    enabled: !!productId,
  })
}

export function useChecklistTemplates() {
  return useQuery({
    queryKey: TEMPLATES_QUERY_KEY,
    queryFn: () => checklistService.getTemplates(),
  })
}

export function useChecklistStats() {
  return useQuery({
    queryKey: [...CHECKLISTS_QUERY_KEY, 'stats'],
    queryFn: () => checklistService.getChecklistStats(),
  })
}

export function useCreateChecklist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      checklist,
      templateId,
    }: {
      checklist: Omit<ChecklistInsert, 'organization_id'>
      templateId?: string
    }) => checklistService.createChecklist(checklist, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLISTS_QUERY_KEY })
    },
  })
}

export function useUpdateChecklist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: ChecklistUpdate }) =>
      checklistService.updateChecklist(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: CHECKLISTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...CHECKLISTS_QUERY_KEY, id] })
    },
  })
}

export function useUpdateChecklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: ChecklistItemUpdate }) =>
      checklistService.updateChecklistItem(id, updates),
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: CHECKLISTS_QUERY_KEY })
        queryClient.invalidateQueries({
          queryKey: [...CHECKLISTS_QUERY_KEY, data.checklist_id, 'items'],
        })
      }
    },
  })
}

export function useDeleteChecklist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => checklistService.deleteChecklist(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLISTS_QUERY_KEY })
    },
  })
}

// ==========================================
// EVIDENCE HOOKS
// ==========================================

export function useChecklistEvidence(checklistId: string) {
  return useQuery({
    queryKey: [...CHECKLIST_EVIDENCE_QUERY_KEY, checklistId],
    queryFn: () => checklistService.getChecklistEvidence(checklistId),
    enabled: UUID_RE.test(checklistId),
  })
}

export function useChecklistItemEvidence(itemId: string) {
  return useQuery({
    queryKey: [...CHECKLIST_EVIDENCE_QUERY_KEY, 'item', itemId],
    queryFn: () => checklistService.getItemEvidence(itemId),
    enabled: !!itemId,
  })
}

export function useLinkEvidence() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (evidence: ChecklistEvidenceInsert) => checklistService.linkEvidence(evidence),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLIST_EVIDENCE_QUERY_KEY })
    },
  })
}

export function useUnlinkEvidence() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (evidenceId: string) => checklistService.unlinkEvidence(evidenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLIST_EVIDENCE_QUERY_KEY })
    },
  })
}

export function useUpdateEvidenceNotes() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ evidenceId, notes }: { evidenceId: string; notes: string }) =>
      checklistService.updateEvidenceNotes(evidenceId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHECKLIST_EVIDENCE_QUERY_KEY })
    },
  })
}
